import { jwtDecode } from 'jwt-decode';
import Constants from '../Constants.js';
const TARGET_LOCATION = 'api.targetLocation';

/**
 * gets and verifies the Bearer token
 * @param {string} targetLocation
 * @returns {function} redux thunk function
 **/
function verify(targetLocation) {
  return (dispatch, getState, {
    api
  }) => {
    return new Promise((resolve, reject) => {
      dispatch({
        type: Constants.AUTH_VERIFYING,
        credentials: null
      });
      if (targetLocation !== undefined) {
        setTargetLocation(targetLocation);
      }
      api.verify().then(result => {
        if (result.revokedAt !== undefined) {
          result.username = result.id;
          result.id = `${result.createdBy}::${result.id}`;
        }
        const payload = jwtDecode(result.token);
        if (payload.type === Constants.AUTH_PROFILE_TYPE_2FA) {
          dispatch({
            type: Constants.AUTH_REQUIRES_2FA,
            profile: result,
            idToken: result.token
          });
        } else {
          // set auth
          dispatch({
            type: Constants.AUTH_LOGGED_IN,
            profile: result,
            idToken: result.token
          });
        }
        resolve(result);
      }, err => {
        dispatch({
          type: Constants.AUTH_VERIFY_FAILED,
          error: err
        });
        reject(err);
      });
    });
  };
}

/**
 * will log in the user
 * @param {string} username 
 * @param {string} password 
 * @param {string?} projectId imagine projectId, will use the project id from the api object passed to redux thunk if none provided
 * @returns {function} redux thunk function
 */

function login(username, password, projectId, options) {
  return (dispatch, getState, {
    api
  }) => {
    return new Promise((resolve, reject) => {
      const userCode = getState().auth?.deviceAccessTokenRequest?.userCode;
      let opts = options;
      if (userCode) {
        opts = opts ? {
          ...opts,
          userCode
        } : {
          userCode
        };
      }
      const credentials = {
        username,
        password
      };
      if (projectId) {
        credentials.projectId = projectId;
      }
      dispatch({
        type: Constants.AUTH_LOGGING_IN,
        credentials
      });
      api.login(username, password, projectId, opts).then(result => {
        if (result.revokedAt !== undefined) {
          result.username = result.id;
          result.id = `${result.createdBy}::${result.id}`;
        }
        if (result.code === Constants.AUTH_DEVICE_CODE_SUCCESS_CODE_TYPE) {
          dispatch({
            type: Constants.AUTH_LOGGED_IN_DEVICE_CODE,
            result
          });
          resolve(result);
          return;
        }
        const payload = jwtDecode(result.token);
        if (payload.type === Constants.AUTH_PROFILE_TYPE_2FA) {
          const info = result.message;
          delete result.message;
          dispatch({
            type: Constants.AUTH_REQUIRES_2FA,
            profile: result,
            info,
            idToken: result.token
          });
        } else if (payload.type === Constants.AUTH_PROFILE_TYPE_2FA_REQUIRE_ENROLL) {
          const info = result.message;
          delete result.message;
          dispatch({
            type: Constants.AUTH_REQUIRES_2FA_ENROLL,
            profile: result,
            info,
            idToken: result.token
          });
        } else {
          // set auth
          dispatch({
            type: Constants.AUTH_LOGGED_IN,
            profile: result,
            idToken: result.token
          });
        }
        resolve(result);
      }).catch(err => {
        dispatch({
          type: Constants.AUTH_LOGIN_FAILED,
          credentials,
          message: err.message,
          exception: err
        });
        reject(err);
      });
    });
  };
}

/**
 * will log the user out
 * @returns {function} redux thunk function
 */

function logout() {
  return (dispatch, getState, {
    api
  }) => {
    dispatch({
      type: Constants.AUTH_LOGGING_OUT
    });
    return api.logout().then(data => {
      dispatch({
        type: Constants.AUTH_LOGGED_OUT
      });
    }, err => {
      dispatch({
        type: Constants.AUTH_LOG_OUT_FAILED,
        message: err.message
      });
    });
  };
}
function reset2FAVerify() {
  return {
    type: Constants.AUTH_RESET_2FA_VERIFY
  };
}

/**
 * sends an email to reset the password
 * @param {string} username the user that the email will be sent to
 * @param {string?} projectId imagine projectId, will use the project id from the api object passed to redux thunk if none provided
 * @returns {function} redux thunk function
 */

function forgotPassword(username, projectId, options) {
  return (dispatch, getState, {
    api
  }) => {
    dispatch({
      type: Constants.AUTH_SENDING_FORGOT_PASSWORD
    });
    return api.forgotPassword(username, projectId, options).then(d => {
      dispatch({
        type: Constants.AUTH_SENT_FORGOT_PASSWORD
      });
      return d;
    }, err => {
      dispatch({
        type: Constants.AUTH_SEND_FORGOT_PASSWORD_FAILED,
        message: err.message
      });
      throw err;
    });
  };
}

/**
 * change the currently logged in user's password
 * @param {string} oldPassword 
 * @param {string} newPassword 
 * @returns {function} redux thunk function
 */

function changePassword(oldPassword, newPassword) {
  return (dispatch, getState, {
    api
  }) => {
    const userId = getState().auth.profile.id;
    dispatch({
      type: Constants.AUTH_CHANGING_PASSWORD
    });
    return api.user(userId).changePassword(oldPassword, newPassword).then(d => {
      dispatch({
        type: Constants.AUTH_CHANGED_PASSWORD
      });
      return d;
    }, err => {
      dispatch({
        type: Constants.AUTH_CHANGE_PASSWORD_FAILED,
        message: err.message,
        exception: err
      });
      throw err;
    });
  };
}

/**
 * changes the user's password
 * @param {string} username 
 * @param {string} password 
 * @param {string} confirm 2nd password entry to confirm first is correct
 * @param {string} token api generated token
 * @param {string} projectId 
 * @returns {function} redux thunk function
 */

function resetPassword(username, password, confirm, token, projectId) {
  return (dispatch, getState, {
    api
  }) => {
    dispatch({
      type: Constants.AUTH_RESETTING_PASSWORD
    });
    return api.resetPassword({
      username,
      password,
      confirm,
      token,
      projectId
    }).then(d => {
      dispatch({
        type: Constants.AUTH_RESET_PASSWORD
      });
      return d;
    }, err => {
      dispatch({
        type: Constants.AUTH_RESET_PASSWORD_FAILED,
        message: err.message,
        exception: err
      });
      throw err;
    });
  };
}

/**
 * changes the user's password
 * @param {string} username 
 * @param {string} password 
 * @param {string} confirm 2nd password entry to confirm first is correct
 * @param {string} code api generated code
 * @param {string} projectId 
 * @returns {function} redux thunk function
 */

function resetPasswordWithCode(username, password, confirm, code, projectId) {
  return (dispatch, getState, {
    api
  }) => {
    dispatch({
      type: Constants.AUTH_RESETTING_PASSWORD
    });
    return api.resetPasswordWithCode({
      username,
      password,
      confirm,
      code,
      projectId
    }).then(d => {
      dispatch({
        type: Constants.AUTH_RESET_PASSWORD
      });
      return d;
    }, err => {
      dispatch({
        type: Constants.AUTH_RESET_PASSWORD_FAILED,
        message: err.message,
        exception: err
      });
      throw err;
    });
  };
}

/**
 * Requests a magic login code via a method (email, sms, etc)
 * @param {string} clientId
 * @returns {function} redux thunk function
 */
function requestDeviceCode(clientId, projectId) {
  return (dispatch, getState, {
    api
  }) => {
    dispatch({
      type: Constants.AUTH_REQUESTING_DEVICE_CODE
    });
    return api.requestDeviceCode(clientId, projectId).then(d => {
      dispatch({
        type: Constants.AUTH_REQUESTED_DEVICE_CODE,
        ...d
      });
      return d;
    }, err => {
      dispatch({
        type: Constants.AUTH_REQUEST_DEVICE_CODE_FAILED,
        message: err.message,
        exception: err
      });
      throw err;
    });
  };
}
function confirmUserCode(userCode, clientId, projectId) {
  return (dispatch, getState, {
    api
  }) => {
    dispatch({
      type: Constants.AUTH_REQUESTING_DEVICE_CODE
    });
    return api.confirmUserCode(userCode, clientId, projectId).then(d => {
      dispatch({
        type: Constants.AUTH_REQUESTED_DEVICE_CODE,
        ...d
      });
      return d;
    }, err => {
      dispatch({
        type: Constants.AUTH_REQUEST_DEVICE_CODE_FAILED,
        message: err.message,
        exception: err
      });
      throw err;
    });
  };
}
function setUserCode(userCode) {
  return dispatch => {
    dispatch({
      type: Constants.AUTH_SET_USER_CODE,
      userCode
    });
  };
}
function clearUserCode() {
  return dispatch => {
    dispatch({
      type: Constants.AUTH_CLEAR_USER_CODE
    });
  };
}
function requestDeviceAccessToken(clientId, projectId) {
  return (dispatch, getState, {
    api
  }) => {
    const {
      auth
    } = getState();
    if (!auth.deviceCodeRequest) {
      const err = new Error('Cannot request device access token before requesting device code');
      dispatch({
        type: Constants.AUTH_REQUEST_DEVICE_ACCESS_TOKEN_FAILED,
        message: err.message,
        exception: err
      });
      throw err;
    }
    dispatch({
      type: Constants.AUTH_REQUESTING_DEVICE_ACCESS_TOKEN
    });
    const {
      deviceCode
    } = auth.deviceCodeRequest;
    return api.requestDeviceAccessToken(deviceCode, clientId, projectId).then(result => {
      dispatch({
        type: Constants.AUTH_LOGGED_IN,
        profile: result,
        idToken: result.token
      });
      return result;
    }).catch(err => {
      dispatch({
        type: Constants.AUTH_REQUEST_DEVICE_ACCESS_TOKEN_FAILED,
        message: err.message,
        exception: err
      });
      throw err;
    });
  };
}

/**
 * Requests a magic login code via a method (email, sms, etc)
 * @param {string} user
 * @param {string} method
 * @param {string} emailTemplate
 * @returns {function} redux thunk function
 */
function requestLoginCode(username, method, emailTemplate) {
  return (dispatch, getState, {
    api
  }) => {
    dispatch({
      type: Constants.AUTH_REQUESTING_LOGIN_CODE
    });
    return api.requestLoginCode(username, method, emailTemplate).then(d => {
      dispatch({
        type: Constants.AUTH_REQUESTED_LOGIN_CODE
      });
      return d;
    }, err => {
      dispatch({
        type: Constants.AUTH_REQUEST_LOGIN_CODE_FAILED,
        message: err.message
      });
      throw err;
    });
  };
}

/**
 * Submits a magic login code
 * @param {string} user
 * @param {string} method
 * @returns {function} redux thunk function
 */
function loginWithCode(username, code, options) {
  return (dispatch, getState, {
    api
  }) => {
    const userCode = getState().auth?.deviceAccessTokenRequest?.userCode;
    let opts = options;
    if (userCode) {
      opts = opts ? {
        ...opts,
        userCode
      } : {
        userCode
      };
    }
    dispatch({
      type: Constants.AUTH_SUBMITTING_LOGIN_CODE
    });
    return api.loginWithCode(username, code, undefined, opts).then(result => {
      if (result.code === Constants.AUTH_DEVICE_CODE_SUCCESS_CODE_TYPE) {
        dispatch({
          type: Constants.AUTH_LOGGED_IN_DEVICE_CODE,
          result
        });
        return result;
      }
      const payload = jwtDecode(result.token);
      if (payload.type === Constants.AUTH_PROFILE_TYPE_2FA) {
        const info = result.message;
        delete result.message;
        dispatch({
          type: Constants.AUTH_REQUIRES_2FA,
          profile: result,
          info,
          idToken: result.token
        });
      } else {
        // set auth
        dispatch({
          type: Constants.AUTH_LOGGED_IN,
          profile: result,
          idToken: result.token
        });
      }
      return result;
    }).catch(err => {
      dispatch({
        type: Constants.AUTH_SUBMIT_LOGIN_CODE_FAILED,
        message: err.message
      });
      throw err;
    });
  };
}
function loginWith2FA(code, method, rememberMe, projectId, options) {
  return (dispatch, getState, {
    api
  }) => {
    return new Promise((resolve, reject) => {
      const userCode = getState().auth?.deviceAccessTokenRequest?.userCode;
      const credentials = {
        code
      };
      if (projectId) {
        credentials.projectId = projectId;
      }
      let opts = options;
      if (userCode) {
        opts = opts ? {
          ...opts,
          userCode
        } : {
          userCode
        };
      }
      dispatch({
        type: Constants.AUTH_LOGGING_IN_WITH_2FA,
        credentials
      });
      api.loginWith2FA(code, method, rememberMe, projectId, opts).then(result => {
        if (result.code === Constants.AUTH_DEVICE_CODE_SUCCESS_CODE_TYPE) {
          dispatch({
            type: Constants.AUTH_LOGGED_IN_DEVICE_CODE,
            result
          });
        } else {
          dispatch({
            type: Constants.AUTH_LOGGED_IN,
            profile: result,
            idToken: result.token,
            rememberMeToken: result.rememberMeToken
          });
        }
        resolve(result);
      }).catch(err => {
        dispatch({
          type: Constants.AUTH_LOGIN_WITH_2FA_FAILED,
          credentials,
          message: err.message
        });
        reject(err);
      });
    });
  };
}

/**
 * Activates a users account
 * @param {object} data - user data
 */
function activateAccount(data) {
  return async (dispatch, getState, {
    api
  }) => {
    dispatch({
      type: Constants.AUTH_ACTIVATING_ACCOUNT
    });
    return new Promise((resolve, reject) => {
      api.activateAccount(data).then(d => {
        const {
          username,
          password
        } = data;
        dispatch({
          type: Constants.AUTH_ACTIVATED_ACCOUNT,
          credentials: {
            username,
            password
          }
        });
        resolve(d);
      }).catch(error => {
        dispatch({
          type: Constants.AUTH_ACTIVATE_ACCOUNT_FAILED,
          message: error.message
        });
        reject(error);
      });
    });
  };
}

/**
 * @param {number} auto
 * @returns {function} redux thunk function
 */
function renew(auto = 0) {
  return (dispatch, getState, {
    api
  }) => {
    return new Promise((resolve, reject) => {
      const userCode = getState().auth?.deviceAccessTokenRequest?.userCode;
      dispatch({
        type: Constants.AUTH_RENEWING
      });
      api.verify({
        user_code: userCode
      }).then(p => {
        if (p.token) {
          dispatch({
            type: Constants.AUTH_RENEWED,
            profile: p,
            idToken: p.token
          });
        } else {
          dispatch({
            type: Constants.AUTH_LOGGED_IN_DEVICE_CODE,
            result: p
          });
        }
        resolve(p);
      }, err => {
        console.error(err);
        reject(err);
      });
    });
  };
}

/* eslint-disable */
function cancelAutoRenew() {
  cancelAutoRenew = 0;
  return () => {
    // do nothing
  };
}
let target = null;
function setTargetLocation(targetLocation) {
  if (typeof localStore !== 'undefined') {
    if (targetLocation) {
      localStore.setItem(TARGET_LOCATION, targetLocation);
    } else {
      localStore.removeItem(TARGET_LOCATION);
    }
  } else {
    target = targetLocation;
  }
}

/* Where do these belong?? */

function getTargetLocation() {
  return typeof localStore !== 'undefined' ? localStore.getItem(TARGET_LOCATION) : target;
}
/* eslint-enable */

/**
 * checks if there is a user logged in
 * @param {object} state the current Auth redux state
 * @returns {boolean} if there is a user logged in or not
 */
function isLoggedIn(state) {
  return state && state.profile != null && state.idToken != null && state.requires2FA !== true && state.requires2FAEnroll !== true;
}

/**
 * checks if the ui is attempting to log in or verify a user
 * @param {object} state the current Auth redux state
 * @returns {boolean} true if loading property is verifying or logging in
 */
function isLoading(state) {
  return state && (state.loading === Constants.AUTH_VERIFYING || state.loading === Constants.AUTH_LOGGING_IN || state.loading === Constants.AUTH_LOGGING_IN_WITH_2FA || state.loading === Constants.AUTH_SUBMITTING_LOGIN_CODE);
}

/**
 * checks if the ui is attempting to verify a user
 * @param {object} state the current Auth redux state
 * @returns {boolean} if loading property is verifying
 */
function isVerifying(state) {
  return state && state.loading === Constants.AUTH_VERIFYING;
}

/**
 * checks if there was an error on the most recent action taken
 * @param {object} state the current Auth redux state
 * @returns {boolean} true if error occured
 */
function isError(state) {
  return state && state.error;
}

/**
 * @param {object} state the current Auth redux state
 * @returns {string} the message/reason for error
 */
function getErrorMessage(state) {
  return state && state.message;
}

/**
 * @param {object} state the current Auth redux state
 * @returns {string} username of currently logged in user
 */
function getUsername(state) {
  return state && state.profile ? state.profile.username : null;
}

/**
 * @param {object} state the current Auth redux state
 * @returns {string} id of currently logged in user
 */
function getUserId(state) {
  return state && state.profile ? state.profile.id : null;
}

/**
 * @param {object} state the current Auth redux state
 * @returns {object} profile of the currently logged in user
 */
function getProfile(state) {
  return state && state.profile;
}
export default {
  verify,
  login,
  logout,
  reset2FAVerify,
  renew,
  cancelAutoRenew,
  forgotPassword,
  changePassword,
  resetPassword,
  activateAccount,
  requestLoginCode,
  requestDeviceCode,
  confirmUserCode,
  requestDeviceAccessToken,
  loginWithCode,
  loginWith2FA,
  getTargetLocation,
  setTargetLocation,
  isLoggedIn,
  getUsername,
  getUserId,
  getProfile,
  isLoading,
  isVerifying,
  isError,
  getErrorMessage,
  resetPasswordWithCode,
  setUserCode,
  clearUserCode
};